import { useRefreshSession } from '@portal/appLayer/auth/useRefreshSession';
import dynamic from 'next/dynamic';
import { routes } from 'constants/routes';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';
import { getShopifyContractId } from 'shared/utils/subscriptionV2Helpers';
import { useQueryGetSubscriptionsV2 } from 'state/internal/getSubscriptions';
import { SepaData } from './SepaView/SepaView.types';
import { VenmoData } from './VenmoView/VenmoView.types';
import { datadogRum } from '@datadog/browser-rum';

const HomeView = dynamic(() => import('./HomeView'));
const VenmoView = dynamic(() => import('./VenmoView'));
const SepaView = dynamic(() => import('./SepaView'));
const HomeViewSkeleton = dynamic(() => import('./HomeViewSkeleton/HomeViewSkeleton'), {
  ssr: false
});

const Home = () => {
  const router = useRouter();
  const isMounted = useRef(false);
  const { mutateAsync: refreshSession } = useRefreshSession();

  const {
    data: subscriptions = [],
    isPending: isLoadingSubscriptions,
    isError: isErrorSubscriptions,
    failureCount: failureCountSubscriptions
  } = useQueryGetSubscriptionsV2();

  const venmoData = useMemo(() => {
    const venmoSubs = subscriptions.filter((sub: any) => !!sub.venmo_email);
    // Explicitly use double assertion to VenmoData
    return venmoSubs[0] as unknown as VenmoData;
  }, [subscriptions]);

  const sepaData = useMemo(() => {
    const sepaSubs = subscriptions.filter((sub: any) => !!sub.sepa_email);
    return sepaSubs[0] as unknown as SepaData;
  }, [subscriptions]);

  // refetch the customer object on page load to check for any changes in customer data, tags, etc.
  useEffect(() => {
    // only once
    if (isMounted.current) return;
    isMounted.current = true;

    const run = () => {
      // fire-and-forget; no UI block
      refreshSession().catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      const id = (window as any).requestIdleCallback(run);
      return () => (window as any).cancelIdleCallback?.(id);
    } else {
      const t = setTimeout(run, 0);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if fetching shopify subscriptions failed and there are no legacy subscriptions
  // display a toast
  // ---- Notify on error (lazy import heavy Notification code) ----
  useEffect(() => {
    if (!isErrorSubscriptions) return;

    datadogRum.addError('Failed to fetch subscriptions', {
      error: new Error('Failed to fetch subscriptions'),
      context: {
        subscriptions,
        isLoadingSubscriptions,
        isErrorSubscriptions,
        failureCountSubscriptions
      }
    });

    (async () => {
      const { showNotification } = await import('shared/ui/Notification');
      showNotification('mutationError', {
        title: 'Something went wrong on our end. Please refresh the page or try again later'
      });
    })();
  }, [isErrorSubscriptions, subscriptions, isLoadingSubscriptions, failureCountSubscriptions]);

  // We only decide once we actually have a result (success or terminal error)
  const finishedLoading =
    !isLoadingSubscriptions || isErrorSubscriptions || !!failureCountSubscriptions;

  useEffect(() => {
    if (!finishedLoading) return;

    // If we have a special flow, render it (don’t redirect)
    if (venmoData || sepaData) return;

    const activeSubs = subscriptions.filter((s: any) => s.is_active);
    const hasSingleActiveSub = activeSubs.length === 1;

    // /members -> detail when exactly 1 active and no venmo/sepa
    if (hasSingleActiveSub) {
      const id = getShopifyContractId(activeSubs[0]?.shopify_contract_id || '');
      if (id) {
        router.replace(routes.subscriptionDetail({ id })); // no history bloat
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedLoading, subscriptions, venmoData, sepaData]);

  // ---- Render logic ----
  // While loading (and we haven't decided), show a tiny skeleton
  if (!finishedLoading) {
    return <HomeViewSkeleton />;
  }

  // Error states: either show skeleton or a minimal safe view
  if (isErrorSubscriptions && !subscriptions?.length) {
    return <HomeViewSkeleton />;
  }

  // Special flows
  if (venmoData) return <VenmoView venmoData={venmoData} />;
  if (sepaData) return <SepaView sepaData={sepaData} />;

  return <HomeView subscriptions={subscriptions} />;
};

export default Home;
